/**
 * Auto Logout Hook
 * 
 * 은행 홈페이지 스타일 자동 로그아웃 기능
 * - 20분간 활동이 없으면 10분 카운트다운 모달 표시
 * - 00:00이 되면 자동 로그아웃 후 "자동 로그아웃됨" 알림과 로그인 유도
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// 설정 (밀리초 단위)
const IDLE_TIMEOUT = 20 * 60 * 1000; // 20분 비활동 시 경고 모달 표시
const WARNING_DURATION = 10 * 60 * 1000; // 10분 카운트다운
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
const THROTTLE_DELAY = 1000; // 활동 감지 스로틀 (1초)

interface UseAutoLogoutOptions {
  onLogout: () => void;
  isAuthenticated: boolean;
  enabled?: boolean;
  idleTimeout?: number;
  warningDuration?: number;
}

interface AutoLogoutState {
  showWarning: boolean;
  showLoggedOutModal: boolean;
  remainingTime: number;
  extendSession: () => void;
  logoutNow: () => void;
  closeLoggedOutModal: () => void;
}

export function useAutoLogout({
  onLogout,
  isAuthenticated,
  enabled = true,
  idleTimeout = IDLE_TIMEOUT,
  warningDuration = WARNING_DURATION,
}: UseAutoLogoutOptions): AutoLogoutState {
  const [showWarning, setShowWarning] = useState(false);
  const [showLoggedOutModal, setShowLoggedOutModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(warningDuration);
  
  const lastActivityRef = useRef<number>(Date.now());
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isThrottledRef = useRef(false);
  const showWarningRef = useRef(showWarning);
  
  // showWarning ref 동기화
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  // 모든 타이머 정리
  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // 로그아웃 후 모달 닫기
  const closeLoggedOutModal = useCallback(() => {
    setShowLoggedOutModal(false);
  }, []);

  // 자동 로그아웃 처리 (00:00 도달 시)
  const handleAutoLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setRemainingTime(warningDuration);
    setShowLoggedOutModal(true);
    onLogout();
  }, [onLogout, clearAllTimers, warningDuration]);

  // remainingTime이 0이 되면 자동 로그아웃 (핵심 수정!)
  useEffect(() => {
    if (showWarning && remainingTime <= 0) {
      handleAutoLogout();
    }
  }, [showWarning, remainingTime, handleAutoLogout]);

  // 카운트다운 시작 함수
  const startCountdown = useCallback(() => {
    // 기존 카운트다운 정리
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    
    setShowWarning(true);
    setRemainingTime(warningDuration);
    
    // 카운트다운 인터벌 (1초마다 감소)
    countdownRef.current = setInterval(() => {
      setRemainingTime(prev => {
        const next = prev - 1000;
        return next < 0 ? 0 : next;
      });
    }, 1000);
  }, [warningDuration]);

  // 세션 연장 (경고 모달에서 호출)
  const extendSession = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setRemainingTime(warningDuration);
    lastActivityRef.current = Date.now();
    
    // 새로운 idle 타이머 시작
    idleTimerRef.current = setTimeout(() => {
      startCountdown();
    }, idleTimeout);
  }, [idleTimeout, warningDuration, clearAllTimers, startCountdown]);

  // 즉시 로그아웃 (수동)
  const logoutNow = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setRemainingTime(warningDuration);
    onLogout();
  }, [onLogout, clearAllTimers, warningDuration]);

  // 사용자 활동 감지 (ref 사용으로 클로저 문제 해결)
  const handleActivity = useCallback(() => {
    if (!enabled || !isAuthenticated) return;
    
    // 경고 모달이 표시 중이면 무시 (모달에서만 연장 가능)
    if (showWarningRef.current) return;
    
    // 스로틀링
    if (isThrottledRef.current) return;
    isThrottledRef.current = true;
    setTimeout(() => {
      isThrottledRef.current = false;
    }, THROTTLE_DELAY);
    
    lastActivityRef.current = Date.now();
    
    // 기존 idle 타이머 리셋
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    
    // 새로운 idle 타이머 시작
    idleTimerRef.current = setTimeout(() => {
      startCountdown();
    }, idleTimeout);
  }, [enabled, isAuthenticated, idleTimeout, startCountdown]);

  // 이벤트 리스너 등록
  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    // 초기 타이머 시작
    lastActivityRef.current = Date.now();
    idleTimerRef.current = setTimeout(() => {
      startCountdown();
    }, idleTimeout);

    // 이벤트 리스너 등록
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
  }, [enabled, isAuthenticated, idleTimeout, handleActivity, clearAllTimers, startCountdown]);

  // 탭 visibility 변경 감지
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastActivityRef.current;
        
        if (elapsed >= idleTimeout + warningDuration) {
          // 이미 타임아웃됨 - 자동 로그아웃
          handleAutoLogout();
        } else if (elapsed >= idleTimeout) {
          // 경고 상태로 전환
          const remaining = warningDuration - (elapsed - idleTimeout);
          if (remaining > 0) {
            // 기존 카운트다운 정리
            if (countdownRef.current) {
              clearInterval(countdownRef.current);
            }
            
            setShowWarning(true);
            setRemainingTime(remaining);
            
            // 카운트다운 시작
            countdownRef.current = setInterval(() => {
              setRemainingTime(prev => {
                const next = prev - 1000;
                return next < 0 ? 0 : next;
              });
            }, 1000);
          } else {
            handleAutoLogout();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isAuthenticated, idleTimeout, warningDuration, handleAutoLogout]);

  return {
    showWarning,
    showLoggedOutModal,
    remainingTime,
    extendSession,
    logoutNow,
    closeLoggedOutModal,
  };
}

export default useAutoLogout;
